import type { Resolution } from '@mimir/common/transcoding/types';
import type { DrawContext } from '@mimir/cutter/compositor/drawContext';
import type { OpaqueRefCountedBuffer, VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import { ImageBitmapBuffer } from './imageBitmapBuffer';
import { WebGlWrapper, LinkedProgram } from './webGlWrapper';
import { getVideoFrameShaderAccess, VideoFrameShaderReadAccess } from './videoFrameShaderReadAccess';
import { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';

export class WebGlWrapperWithCanvas extends WebGlWrapper {
  public readonly canvas: HTMLCanvasElement;
  public readonly offscreenCanvas: OffscreenCanvas;
  constructor(logger: Logger) {
    const canvas = document.createElement('canvas');
    const offscreenCanvas = canvas.transferControlToOffscreen();
    super(logger, offscreenCanvas);
    this.offscreenCanvas = offscreenCanvas;
    this.canvas = canvas;
  }
}

export class ImageBitmapBasedBrowserGlDrawContext implements DrawContext {
  webGlWrapper : WebGlWrapperWithCanvas;
  constructor(private readonly bufferCache: BufferCache, logger: Logger) {
    this.webGlWrapper = new WebGlWrapperWithCanvas(logger);
  }

  scale(input: VideoFrameV2, resolution: Resolution): Promise<VideoFrameV2> {
    const canvas = this.webGlWrapper.offscreenCanvas;
    if (canvas.width !== resolution.width || canvas.height !== resolution.height) {
      canvas.width = resolution.width;
      canvas.height = resolution.height;
    }

    return renderResizedFrame(input, this.webGlWrapper, this.bufferCache, resolution);
  }
}

export class LazyBrowserGlDrawContext implements DrawContext{
  constructor(private readonly webGlWrapper: WebGlWrapper, private bufferCache: BufferCache) { }

  async scale(input: VideoFrameV2, targetResolution: Resolution): Promise<VideoFrameV2> {
    const textureSamplingSize = calculateTextureSamplingSize(input.resolution, targetResolution);
    return {
      resolution: targetResolution,
      pixelFormat: 'rgba',
      fieldOrder: 'progressive',
      buffer: new LazyScaleFrame(
        getVideoFrameShaderAccess(this.webGlWrapper, input, this.bufferCache, false),
        targetResolution,
        textureSamplingSize,
        { refCount: 0 }
      )
    };
  }
}

interface TextureSamplingSize {
  scaleX: number;
  scaleY: number;
}

export async function renderResizedFrame(
  sourceVideoFrame: VideoFrameV2,
  webGlWrapper: WebGlWrapperWithCanvas,
  bufferCache: BufferCache,
  targetResolution: Resolution,
): Promise<VideoFrameV2> {
  const gl = webGlWrapper.getGlContext();

  const sourceResolution = sourceVideoFrame.resolution;
  const textureSamplingSize = calculateTextureSamplingSize(sourceResolution, targetResolution);
  const input = getVideoFrameShaderAccess(webGlWrapper, sourceVideoFrame, bufferCache, false);
  const lazyScaleFrame = new LazyScaleFrame(input, targetResolution, textureSamplingSize, { refCount: 0 });
  const shaderReadAccess = lazyScaleFrame.getShaderReadAccess(webGlWrapper, bufferCache, false);
  drawScaledGlTexture(webGlWrapper, gl, targetResolution, shaderReadAccess, false);
  lazyScaleFrame.close();

  const bitmapPromise = createImageBitmap(webGlWrapper.offscreenCanvas);
  webGlWrapper.throwIfError();
  const bitmap = await bitmapPromise;

  return {
    buffer: new ImageBitmapBuffer(bitmap),
    resolution: targetResolution,
    pixelFormat: 'rgba',
    fieldOrder: 'progressive'
  };
}

/**
 * Get the span of of texture coordinates to sample in order to scale the source
 * image to fit into the target resolution. Whenever a the texture sampling
 * width or height is greater than 1.0, it means that the source image will be
 * letterboxed or pillarboxed to fit into the target resolution. I.e. such
 * numbers represent trying to sample a texture outside of its bounds, which our
 * fragment shader will handle by returning a black color.
 */
export function calculateTextureSamplingSize(sourceResolution: Resolution, targetResolution: Resolution): TextureSamplingSize {
  const sourceAspectRatio = sourceResolution.width / sourceResolution.height;
  const targetAspectRatio = targetResolution.width / targetResolution.height;

  let scaleX: number;
  let scaleY: number;
  if (sourceAspectRatio > targetAspectRatio) {
    // Source is wider than target, we need to letterbox Example:
    // * sourceAspectRatio = 16/9, targetAspectRatio = 4/3
    // * scaleX = 1, scaleY = (16/9) / (4/3) = 1.3333
    // * I.e. 33.33% will be added to the source scaleY in the form of letterbox
    //   bars at the top and bottom.
    scaleX = 1;
    scaleY = sourceAspectRatio / targetAspectRatio;

  } else {
    // Source is taller than target, we need to pillarbox
    scaleY = 1;
    scaleX = targetAspectRatio / sourceAspectRatio;
  }

  return { scaleX, scaleY };
}


export class LazyScaleFrame implements OpaqueRefCountedBuffer {
  private closed = false;

  public static tryGet(videoFrame: VideoFrameV2): LazyScaleFrame | null {
    if (videoFrame.buffer instanceof LazyScaleFrame) {
      return videoFrame.buffer;
    }
    return null;
  }

  constructor(
    private input: VideoFrameShaderReadAccess,
    private resolution: Resolution,
    private textureSamplingSize: TextureSamplingSize,
    private state: { refCount: number }
  ) {
    state.refCount ++;
  }

  private checkClosed() {
    if (this.closed) {
      throw new Error('LazyScaleFrame is closed');
    }
  }

  copyTo(_bytes: Uint8Array): Promise<void> {
    this.checkClosed();
    // TODO: Make sure that the implementation of this matches the size
    // reported by size()
    throw new Error('TODO: LazyScaleFrame.copyTo');
  }

  close(): void {
    this.checkClosed();
    this.state.refCount --;
    if (this.state.refCount <= 0) {
      this.input.close();
    }
    this.closed = true;
  }

  size(): number {
    this.checkClosed();
    // Assume if we implement copyTo that we'd convert to 8-bit RGBA
    return this.resolution.width * this.resolution.height * 4;
  }

  addReference(): OpaqueRefCountedBuffer {
    this.checkClosed();
    return new LazyScaleFrame(this.input, this.resolution, this.textureSamplingSize, this.state);
  }

  getShaderReadAccess(
    _webGlWrapper: WebGlWrapper,
    _bufferCache: BufferCache,
    _forceSystemMemorySource: boolean
  ): VideoFrameShaderReadAccess {
    this.checkClosed();
    const textureSamplingSize = this.textureSamplingSize;

    let closed = false;

    return {
      readFunctionName: 'lazyFrameRead',
      readFunctionSource: `

${this.input.readFunctionSource}

uniform vec2 uvSize;

vec4 lazyFrameRead(vec2 unscaledUv) {
  vec2 uv = ((unscaledUv - 0.5) * uvSize) + 0.5;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    return vec4(0.0, 0.0, 0.0, 1.0); // Black color for out-of-bounds UVs
  } else {
    return ${this.input.readFunctionName}(uv);
  }
}
      `,
      bind: (gl: WebGL2RenderingContext, program: LinkedProgram, textureSlotOffset: number) => {
        if (closed) {
          throw new Error('LazyScaleFrame shader read access already closed');
        }
        this.input.bind(gl, program, textureSlotOffset);
        const uvSizeLoc = program.getUniformLocation(gl, 'uvSize');
        invariant(uvSizeLoc !== null, 'uvSize uniform location is null');
        gl.uniform2f(uvSizeLoc, textureSamplingSize.scaleX, textureSamplingSize.scaleY);
      },
      close() {
        if (closed) {
          throw new Error('LazyScaleFrame shader read access already closed');
        }
        closed = true;
      }
    };
  }
}





function buildFragmentShaderSource(shaderReadAccess: VideoFrameShaderReadAccess) {
  return `#version 300 es
precision mediump float;
in vec2 uv;
out vec4 color;

${shaderReadAccess.readFunctionSource}

void main() {
  color = ${shaderReadAccess.readFunctionName}(uv);
}
  `;
}

const vertexShaderSource = `#version 300 es

out vec2 uv;
uniform mat2 transform;

void main() {
  vec2 positions[6] = vec2[](
    vec2(-1.0, -1.0),
    vec2(1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, 1.0)
  );
  vec2 uvs[6] = vec2[](
    vec2(0.0, 1.0),
    vec2(1.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 0.0)
  );
  gl_Position = vec4(positions[gl_VertexID] * transform, vec2(0.0, 1.0));
  uv = uvs[gl_VertexID];
}`;

const transformIdentity = new Float32Array([1.0, 0.0, 0.0, 1.0]);
const transformFlipVertically = new Float32Array([1.0, 0.0, 0.0, -1.0]);

export function drawScaledGlTexture(
  webGlWrapper: WebGlWrapper,
  gl: WebGL2RenderingContext,
  resolution: Resolution,
  shaderReadAccess: VideoFrameShaderReadAccess,
  flipVertically: boolean,
) {
  const fragmentShaderSource = buildFragmentShaderSource(shaderReadAccess);
  const linkedProgram = webGlWrapper.getLinkedProgram(vertexShaderSource, fragmentShaderSource);
  let textureSlot = 0;
  linkedProgram.use(gl);
  shaderReadAccess.bind(gl, linkedProgram, textureSlot);
  webGlWrapper.throwIfError();

  gl.viewport(0, 0, resolution.width, resolution.height);
  webGlWrapper.throwIfError();
  const transformLoc = linkedProgram.getUniformLocation(gl, 'transform');
  gl.uniformMatrix2fv(transformLoc, false, flipVertically ? transformFlipVertically : transformIdentity);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  webGlWrapper.throwIfError();
  shaderReadAccess.close();
}

